import { useEffect, useRef } from 'react';

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => el.classList.add('visible'), delay);
          obs.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return ref;
}

const reasons = [
  {
    icon: 'ri-search-eye-line',
    title: 'Inspection-Level Knowledge',
    desc: '3 years as a certified home inspector means I spot issues most agents miss — saving buyers money and helping sellers prepare properly.',
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'No Pressure, Just Guidance',
    desc: 'I give honest assessments whether you want to hear them or not. My goal is your best move, not just a fast commission.',
  },
  {
    icon: 'ri-award-line',
    title: 'First-Time Buyer Friendly',
    desc: 'I take the time to explain every step. You\'ll never feel rushed, confused, or out of the loop when we work together.',
  },
  {
    icon: 'ri-building-2-line',
    title: 'Estate & Probate Support',
    desc: 'Selling a family home or inherited property? I handle the details with care, respect, and genuine concern for your family.',
  },
  {
    icon: 'ri-arrow-left-right-line',
    title: 'Buy & Sell Transitions',
    desc: 'Coordinating a move while buying and selling simultaneously? I manage the timing and logistics so you don\'t have to stress.',
  },
  {
    icon: 'ri-landscape-line',
    title: 'Land & Specialty Sales',
    desc: 'Vacant land, hunting property, rural acreage — I know this market and the unique considerations that come with it.',
  },
];

export default function HomeWhyKendrick() {
  const headingRef = useReveal(0);
  const card0 = useReveal(0);
  const card1 = useReveal(80);
  const card2 = useReveal(160);
  const card3 = useReveal(0);
  const card4 = useReveal(80);
  const card5 = useReveal(160);
  const cardRefs = [card0, card1, card2, card3, card4, card5];

  return (
    <section className="py-14 sm:py-20 bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <div ref={headingRef} className="reveal text-center max-w-2xl mx-auto mb-10 sm:mb-14">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 font-medium">What Makes Me Different</p>
          <h2 className="font-cormorant text-4xl sm:text-5xl font-light text-[#111111] leading-tight">
            Real Home Knowledge. <em className="not-italic font-semibold">Real Results.</em>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {reasons.map((r, i) => (
            <div
              key={i}
              ref={cardRefs[i]}
              className="reveal scale group"
            >
              <div className="w-10 h-10 flex items-center justify-center mb-4">
                <i className={`${r.icon} text-2xl text-[#1C3A6B]`}></i>
              </div>
              <h3 className="font-cormorant text-2xl font-semibold text-[#111111] mb-3">{r.title}</h3>
              <div className="w-8 h-0.5 bg-[#C8923A] mb-4 group-hover:w-14 transition-all duration-300"></div>
              <p className="text-sm text-[#666] leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
